import {Box, Chip, IconButton, Paper, Stack, ToggleButton, ToggleButtonGroup, Tooltip} from "@mui/material";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {useQuery} from "react-query";
import {
    SimphonyEvent,
    SimphonyEventArea
} from "crqlar-react-common/dist/models/integrations/simphony/simphonyEvent.model";
import {SimphonyEventService} from "crqlar-react-common/dist/services/integrations/simphony/simphonyEvent.service";
import {formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";
import EmptyState from "crqlar-react-common/dist/components/EmptyState";

export const SIMPHONY_EVENTS_QUERY_KEY = "simphonyEvents";

const PAGE_SIZE = 25;

/** Problems keep arriving while the tab is open (a sync every few minutes, a seating any time). */
const REFRESH_MS = 30_000;

const AREA_LABELS: Record<SimphonyEventArea, string> = {
    AUTHENTICATION: 'Sign-in',
    POS_TERMINAL: 'POS workstation',
    CHECK_SYNC: 'Reading checks',
    CHECK_WRITE: 'Reservation on check',
    CHECK_MOVE: 'Table move',
    RESERVATION: 'Reservation',
    CONFIGURATION: 'Configuration',
};

interface Props {
    configId: string
}

/**
 * The Simphony connector's error log: every problem crqlar ran into with Simphony, newest first. One
 * row per problem — a till that stays off is one row with a count — and a row closes by itself once
 * the same thing works again ("Resolved"). Backed by `simphonyEvents` in the API.
 *
 * See `features/simphony-error-log.md`.
 */
const SimphonyErrorLog = ({configId}: Props) => {

    const {t} = useTranslation();
    const [openOnly, setOpenOnly] = useState(false);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);

    const eventsQuery = useQuery(
        [SIMPHONY_EVENTS_QUERY_KEY, configId, openOnly, pageSize],
        () => SimphonyEventService.getAll({
            configId,
            openOnly,
            limit: {offset: 0, limit: pageSize, sort: [{field: 'lastSeenAt', direction: 'DESC'}]}
        }),
        {keepPreviousData: true, refetchInterval: REFRESH_MS}
    );

    const events = eventsQuery.data?.content ?? [];
    const total = eventsQuery.data?.totalElements ?? 0;

    return (
        <Stack gap={2}>
            <Stack direction={{xs: 'column', sm: 'row'}} gap={2} alignItems={{sm: 'center'}}>
                <Box sx={{flex: 1}}>
                    <Typography variant={'h6'}>{t('Error log')}</Typography>
                    <Typography variant={'body2'} color={'text.secondary'}>
                        {t('Problems crqlar ran into with Simphony: reservations written to POS checks, reading checks, POS workstations offline and signing in. An entry is marked resolved by itself once the same thing works again.')}
                    </Typography>
                </Box>

                <Stack direction={'row'} gap={1} alignItems={'center'}>
                    <ToggleButtonGroup
                        size={'small'}
                        exclusive
                        value={openOnly ? 'open' : 'all'}
                        onChange={(e, value) => {
                            if (!value) return;
                            setOpenOnly(value === 'open');
                            setPageSize(PAGE_SIZE);
                        }}
                    >
                        <ToggleButton value={'all'}>{t('All')}</ToggleButton>
                        <ToggleButton value={'open'}>{t('Open')}</ToggleButton>
                    </ToggleButtonGroup>

                    <Tooltip title={t('Refresh')}>
                        <span>
                            <IconButton onClick={() => eventsQuery.refetch()} disabled={eventsQuery.isFetching}>
                                <RefreshIcon/>
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </Stack>

            {
                !eventsQuery.isLoading && events.length === 0 &&
                <EmptyState
                    icon={<CheckCircleOutlineIcon/>}
                    title={openOnly ? t('No open problems') : t('No problems logged')}
                    description={t('Everything crqlar did with Simphony recently worked.')}
                />
            }

            {events.map(event => <EventRow key={event.id} event={event}/>)}

            {
                events.length < total &&
                <Box sx={{display: 'flex', justifyContent: 'center'}}>
                    <Button onClick={() => setPageSize(size => size + PAGE_SIZE)} disabled={eventsQuery.isFetching}>
                        {t('Show more')} ({total - events.length})
                    </Button>
                </Box>
            }
        </Stack>
    );
};

const EventRow = ({event}: { event: SimphonyEvent }) => {

    const {t} = useTranslation();
    const resolved = !!event.resolvedAt;
    const occurrences = event.occurrences ?? 1;

    const icon = resolved
        ? <CheckCircleOutlineIcon color={'success'}/>
        : event.severity === 'ERROR'
            ? <ErrorOutlineIcon color={'error'}/>
            : <WarningAmberIcon color={'warning'}/>;

    return (
        <Paper variant={'outlined'} sx={{p: 2, opacity: resolved ? 0.8 : 1}}>
            <Stack direction={'row'} gap={1.5} alignItems={'flex-start'}>
                <Box sx={{pt: 0.25}}>{icon}</Box>

                <Stack gap={0.75} sx={{flex: 1, minWidth: 0}}>
                    <Typography variant={'subtitle2'} sx={{wordBreak: 'break-word'}}>
                        {event.title}
                    </Typography>

                    <Stack direction={'row'} gap={1} flexWrap={'wrap'} alignItems={'center'}>
                        <Chip
                            size={'small'}
                            label={resolved ? t('Resolved') : event.severity === 'ERROR' ? t('Error') : t('Warning')}
                            color={resolved ? 'success' : event.severity === 'ERROR' ? 'error' : 'warning'}
                            variant={'outlined'}
                        />
                        {event.area && <Chip size={'small'} label={t(AREA_LABELS[event.area])}/>}
                        {occurrences > 1 && <Chip size={'small'} label={`${occurrences}×`}/>}
                        <Typography variant={'caption'} color={'text.secondary'}>
                            {
                                occurrences > 1
                                    ? `${formatFullDateTime(event.firstSeenAt)} – ${formatFullDateTime(event.lastSeenAt)}`
                                    : formatFullDateTime(event.lastSeenAt)
                            }
                        </Typography>
                    </Stack>

                    {
                        event.message &&
                        <Typography variant={'body2'} color={'text.secondary'} sx={{whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
                            {event.message}
                        </Typography>
                    }

                    {
                        resolved &&
                        <Typography variant={'body2'} color={'success.main'} sx={{wordBreak: 'break-word'}}>
                            {t('Resolved')} {formatFullDateTime(event.resolvedAt)}{event.resolution ? `: ${event.resolution}` : ''}
                        </Typography>
                    }
                </Stack>
            </Stack>
        </Paper>
    );
};

export default SimphonyErrorLog;
